import { useState, useEffect, type CSSProperties } from 'react';
import { Avatar } from '@arco-design/web-react';
import {
  getAvatarProxyUrl,
  resolveAvatarDisplayUrl,
  isDefaultOrMissingAvatar,
  isDefaultAvatarSrc,
  getInitials,
  getInitialsFromFullName,
} from '../../utils/avatar';

import './UserAvatar.css';

export type UserAvatarProps = {
  avatarUrl?: string;
  /** When set, used as a same-origin fallback if the direct avatar URL fails to load. */
  userId?: number;
  firstName?: string;
  lastName?: string;
  /** Use when only full name is available (e.g. "First Last") */
  fullName?: string;
  /** Pixel size for circle, or width/height for square when size is used */
  size?: number;
  shape?: 'circle' | 'square';
  alt?: string;
  className?: string;
  /** Use when container controls dimensions (e.g. width: '100%') */
  style?: React.CSSProperties;
  /** When true, defer loading until near viewport (card grids). */
  lazy?: boolean;
};

/**
 * Shows user photo when available, otherwise initials (first + last) on black with Wild Mango.
 * Shape matches container: circle for list avatars, square for card/profile hero.
 * When a photo URL exists, shows a neutral gray placeholder while the image loads — not initials.
 */
export function UserAvatar({
  avatarUrl,
  userId,
  firstName,
  lastName,
  fullName,
  size,
  shape = 'circle',
  alt = '',
  className = '',
  style,
  lazy = false,
}: UserAvatarProps) {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [src, setSrc] = useState<string | undefined>(() =>
    resolveAvatarDisplayUrl(avatarUrl, userId)
  );
  const effectiveSize = size ?? 64;

  const expectsPhoto = !isDefaultOrMissingAvatar(avatarUrl);

  useEffect(() => {
    setImageError(false);
    setImageLoaded(false);
    setSrc(resolveAvatarDisplayUrl(avatarUrl, userId));
  }, [avatarUrl, userId]);

  const handleImageError = () => {
    const proxyUrl = userId ? getAvatarProxyUrl(userId) : undefined;
    const directUrl = !isDefaultOrMissingAvatar(avatarUrl) ? avatarUrl : undefined;

    if (directUrl && src === directUrl && proxyUrl) {
      setImageLoaded(false);
      setSrc(proxyUrl);
      return;
    }
    if (proxyUrl && src === proxyUrl && directUrl) {
      setImageLoaded(false);
      setSrc(directUrl);
      return;
    }
    setImageError(true);
  };

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const loadedSrc = event.currentTarget.currentSrc || event.currentTarget.src;
    if (isDefaultAvatarSrc(loadedSrc)) {
      handleImageError();
      return;
    }
    setImageLoaded(true);
  };

  const showInitials = !expectsPhoto || imageError || !src;

  const initials =
    firstName != null || lastName != null
      ? getInitials(firstName, lastName)
      : getInitialsFromFullName(fullName);

  if (showInitials) {
    if (!initials) {
      return null;
    }
    /* When size is not passed, treat as fluid (fill container) for big square on cards/profile */
    const hasSize = size != null && size > 0;
    const sizeStyle = hasSize
      ? { width: size, height: size, fontSize: Math.max(12, Math.floor(size * 0.48)) }
      : {
          /* Absolute fill inside clipping wrapper (PersonCard / profile heroes) */
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          margin: 0,
          boxSizing: 'border-box',
        };
    const fluidClass = hasSize ? '' : ' user-avatar-initials--fluid';
    /* Centering is grid + place-items in CSS; don't let callers force display:block */
    const { display: _ignoredDisplay, ...styleRest } = style ?? {};
    const mergedStyle = {
      ...sizeStyle,
      ...styleRest,
    } as CSSProperties;
    return (
      <div
        className={`user-avatar-initials user-avatar-initials--${shape}${fluidClass} ${className}`.trim()}
        style={mergedStyle}
        role="img"
        aria-label={alt || initials}
      >
        <span className="user-avatar-initials__text">{initials}</span>
      </div>
    );
  }

  const loadingClass = imageLoaded ? '' : ' user-avatar-photo--loading';
  const imgProps = {
    src,
    alt,
    referrerPolicy: 'no-referrer' as const,
    loading: lazy ? ('lazy' as const) : ('eager' as const),
    onError: handleImageError,
    onLoad: handleImageLoad,
  };

  if (shape === 'circle') {
    return (
      <div
        className={`user-avatar-photo user-avatar-photo--circle${loadingClass} ${className}`.trim()}
        style={{
          width: effectiveSize,
          height: effectiveSize,
          ...style,
        }}
      >
        <Avatar size={effectiveSize}>
          <img {...imgProps} alt={alt} />
        </Avatar>
      </div>
    );
  }

  return (
    <div
      className={`user-avatar-photo user-avatar-photo--square${loadingClass} ${className}`.trim()}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        margin: 0,
        ...style,
      }}
    >
      <img
        {...imgProps}
        alt={alt}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          margin: 0,
          display: 'block',
          objectFit: 'cover',
          objectPosition: '50% 50%',
        }}
      />
    </div>
  );
}
